/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
/**
 * MODAL "BOT × EMPRESA" do Resumo (pedido 19/10: "quando eu clicar em cima do
 * RPA ... abrir um modal com o erro e o histórico, igual na outra aba, com a
 * função de abrir print").
 *
 * Não reimplementa nada: o histórico é o `HistoricoDaUnidade` e o print é o
 * `BotaoVerPrint` — os MESMOS do popup do Processos, na mesma janela da tela.
 * O que este modal acrescenta é só o cabeçalho com o estado do par e o motivo.
 *
 * Bot que a empresa NÃO percorreu (falta) também abre: o histórico pode mostrar
 * passagem fora da janela, e o modal diz com todas as letras que não há log no
 * período — em vez de um clique que não faz nada.
 */

import React, { useEffect } from 'react';
import type { IBotDaEmpresa } from '../api';
import { C, FONTE_MONO, FONTE_TEXTO, TONES, TONE_DO_STATUS } from '../paleta';
import { ST_META } from '../utils/ordemStatus';
import { dataHoraCompleta } from '../utils/dataFalha';
import { BotaoVerPrint } from './BotaoVerPrint';
import { HistoricoDaUnidade } from './HistoricoDaUnidade';
import { EYEBROW, Faixa, VIDRO } from './Vidro';

export interface AlvoModalBot {
  bot: string;
  codiEmp: string;
  nomeEmpresa: string;
  regime: string | null;
  /** Linha do /resumo; null = bot esperado que a empresa não percorreu. */
  info: IBotDaEmpresa | null;
  /** Área que espera o bot (só para o caso "falta percorrer"). */
  area?: string | null;
}

/** Motivo exibido: o do status atual, com o da última falha como reserva. */
export const motivoDoBot = (b: IBotDaEmpresa): string | null => {
  if (b.status === 'err') return b.motivo_erro || b.ultima_falha_motivo || null;
  if (b.status === 'warn') return b.motivo_aviso || null;
  if (b.status === 'skip') return b.motivo_skip || null;
  return null;
};

export const ModalBotDaEmpresa: React.FC<{
  alvo: AlvoModalBot;
  // eslint-disable-next-line no-unused-vars
  apelidoDoBot: (bot: string) => string;
  de?: string;
  ate?: string;
  onFechar: () => void;
  onAbrirNoProcessos: () => void;
}> = ({ alvo, apelidoDoBot, de, ate, onFechar, onAbrirNoProcessos }) => {
  useEffect(() => {
    const aoTeclar = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onFechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [onFechar]);

  const { info } = alvo;
  const tom = info ? TONES[TONE_DO_STATUS[info.status] ?? 'idle'] : TONES.idle;
  const rotuloStatus = info ? (ST_META[info.status]?.label ?? info.status) : 'Não percorreu';
  const motivo = info ? motivoDoBot(info) : null;
  const ultimaFalha = info ? dataHoraCompleta(info.ultima_falha_em) : null;
  const apelido = apelidoDoBot(alvo.bot);

  return (
    <div
      role="presentation"
      onClick={onFechar}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        background: 'oklch(0.2 0.02 165 / 0.38)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: FONTE_TEXTO,
        color: C.ink,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${apelido} — ${alvo.nomeEmpresa}`}
        onClick={ev => ev.stopPropagation()}
        style={{
          ...VIDRO,
          background: 'linear-gradient(145deg, rgba(255,255,255,0.95), rgba(255,255,255,0.86))',
          borderRadius: '22px',
          width: '100%',
          maxWidth: '760px',
          maxHeight: '88vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: '20px 22px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          boxShadow: '0 30px 90px oklch(0.2 0.02 165 / 0.35)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span
            aria-hidden
            style={{
              flex: 'none',
              width: '40px',
              height: '40px',
              borderRadius: '13px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '16px',
              color: info ? 'white' : C.sub,
              background: info ? tom.dot : 'transparent',
              border: info ? 'none' : `2px dashed ${C.faint}`,
              boxSizing: 'border-box',
            }}
          >
            {info ? '●' : '?'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={EYEBROW}>
              {alvo.codiEmp} · {alvo.nomeEmpresa}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, marginTop: '3px' }}>{apelido}</div>
            {apelido !== alvo.bot && (
              <div style={{ fontFamily: FONTE_MONO, fontSize: '11px', color: C.faint }}>
                {alvo.bot}
              </div>
            )}
          </div>
          <span
            style={{
              padding: '4px 11px',
              borderRadius: '999px',
              background: tom.bg,
              color: tom.fg,
              fontSize: '12px',
              fontWeight: 650,
              whiteSpace: 'nowrap',
            }}
          >
            {rotuloStatus}
          </span>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            style={{
              border: 'none',
              background: 'rgba(16,60,48,0.06)',
              borderRadius: '10px',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              color: C.sub,
              fontSize: '14px',
            }}
          >
            ✕
          </button>
        </div>

        {!info && (
          <Faixa tom="idle">
            Esta empresa não tem nenhum log deste bot no período da tela
            {alvo.area ? `, mas ela está no universo da área ${alvo.area}` : ''} — por isso ele
            aparece como &quot;falta percorrer&quot;. O histórico abaixo mostra se houve passagem.
          </Faixa>
        )}

        {info && (motivo || info.status === 'err' || info.status === 'warn') && (
          <Faixa tom={TONE_DO_STATUS[info.status] ?? 'idle'}>
            <div style={{ fontWeight: 650, marginBottom: '3px' }}>
              {info.status === 'err' && 'Mensagem do erro'}
              {info.status === 'warn' && 'Mensagem do alerta'}
              {info.status === 'skip' && 'Motivo da pendência'}
            </div>
            <div style={{ fontFamily: FONTE_MONO, fontSize: '12px', wordBreak: 'break-word' }}>
              {motivo ?? 'sem mensagem registrada'}
            </div>
          </Faixa>
        )}

        {info && (ultimaFalha || (info.falhas_no_periodo ?? 0) > 0) && (
          <div style={{ display: 'flex', gap: '8px 18px', flexWrap: 'wrap', fontSize: '12px' }}>
            {(info.falhas_no_periodo ?? 0) > 0 && (
              <span>
                <span style={{ color: C.sub }}>Falhas no período: </span>
                <strong style={{ color: C.redFg }}>{info.falhas_no_periodo}</strong>
              </span>
            )}
            {ultimaFalha && (
              <span>
                <span style={{ color: C.sub }}>Última falha: </span>
                <strong style={{ fontFamily: FONTE_MONO }}>{ultimaFalha}</strong>
              </span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <BotaoVerPrint bot={alvo.bot} codiEmp={alvo.codiEmp} de={de} ate={ate} />
          <button
            type="button"
            onClick={onAbrirNoProcessos}
            style={{
              marginLeft: 'auto',
              padding: '7px 13px',
              borderRadius: '10px',
              border: '1px solid oklch(0.55 0.14 240)',
              background: 'linear-gradient(140deg, oklch(0.62 0.14 240), oklch(0.5 0.14 240))',
              color: 'white',
              fontFamily: FONTE_TEXTO,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Ver na esteira (Processos) →
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={EYEBROW}>Histórico</div>
          <HistoricoDaUnidade bot={alvo.bot} codiEmp={alvo.codiEmp} de={de} ate={ate} aberto />
        </div>
      </div>
    </div>
  );
};
